import './PointsLedgerTable.css';
import type { MembershipLedgerEntry } from '../api/membership.api';
import PointsAmount from './PointsAmount';

type PointsLedgerTableProps = {
  rows: MembershipLedgerEntry[];
  loading?: boolean;
  page: number;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
};

const formatDateTime = (value: string): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

type EventConfig = { icon: string; label: string; className: string };
const EVENT_CONFIGS: Record<string, EventConfig> = {
  EARN:   { icon: '↑', label: 'Earn',   className: 'is-earn' },
  REDEEM: { icon: '↓', label: 'Redeem', className: 'is-redeem' },
  ADJUST: { icon: '↔', label: 'Adjust', className: 'is-adjust' },
  EXPIRE: { icon: '✕', label: 'Expire', className: 'is-expire' },
};

const getEventConfig = (eventType: string): EventConfig =>
  EVENT_CONFIGS[String(eventType || '').toUpperCase()] || { icon: '•', label: eventType || '—', className: '' };

const PointsLedgerTable = ({
  rows, loading, page, hasNext, onPrev, onNext
}: PointsLedgerTableProps) => (
  <article className="appointments-v2-card">
    <div className="appointments-v2-card-header">
      <h3>Points ledger</h3>
      <div className="points-ledger__pagination">
        <span className="points-ledger__page-label">Page {page + 1}</span>
        <button type="button" className="btn ghost small" onClick={onPrev} disabled={page <= 0}>Prev</button>
        <button type="button" className="btn ghost small" onClick={onNext} disabled={!hasNext}>Next</button>
      </div>
    </div>
    <div className="appointments-v2-card-body">
      {loading ? <p className="appointments-v2-empty-copy">Loading ledger...</p> : null}
      {!loading && rows.length === 0 ? (
        <div className="points-ledger__empty">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none" aria-hidden="true">
            <rect x="12" y="12" width="40" height="40" rx="8" stroke="currentColor" strokeWidth="1.5" opacity="0.15" />
            <path d="M24 32h16M32 24v16" stroke="currentColor" strokeWidth="1.5" opacity="0.2" strokeLinecap="round" />
          </svg>
          <p className="appointments-v2-empty-copy">No ledger entries found.</p>
        </div>
      ) : null}
      {!loading && rows.length > 0 ? (
        <table className="appointments-v2-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Type</th>
              <th>Points</th>
              <th>Balance after</th>
              <th>Source</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const config = getEventConfig(row.eventType);
              return (
                <tr key={row.ledgerUuid}>
                  <td>{formatDateTime(row.createdAt)}</td>
                  <td>
                    <span className={`points-ledger__event-badge ${config.className}`}>
                      <span className="points-ledger__event-icon" aria-hidden="true">{config.icon}</span>
                      {config.label}
                    </span>
                  </td>
                  <td><PointsAmount value={row.pointsDelta || 0} /></td>
                  <td>
                    <span className="points-ledger__balance">{row.balanceAfter ?? 0}</span>
                  </td>
                  <td>{row.sourceRef || '—'}</td>
                  <td>{row.note || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}
    </div>
  </article>
);

export default PointsLedgerTable;
